import { createApp, ref } from "vue";
import { MdsConfig } from "@maersk-global/mds-config";
import { MaerskVueApp, type MaerskVueAppConfig } from "@maersk-global/vue-plugin-maerskvueapp";
import { VueLanguageHandler } from "@maersk-global/vue-plugin-languagehandler";
import App from "@/App.vue";
import router from "@/router/router";
import { setupGlobals } from "@/utils/globals";
import { version } from "../package.json";
import pinia from "@/store/index";
import { msalPlugin } from "@/plugins/msalPlugin";
import { getMsalInstance } from "@/config/authConfig";
import { setupAuthentication } from "@/authentication/authSetup";
import VueDOMPurifyHTML from "vue-dompurify-html";

function registerServiceWorker() {
  if ("serviceWorker" in navigator) {
    window.addEventListener("load", () => {
      fetch("/sw.js", { method: "HEAD" })
        .then((response) => {
          if (response.ok && response.headers.get("content-type")?.includes("application/javascript")) {
            navigator.serviceWorker.register("/sw.js", { updateViaCache: "none" }).then(
              (registration) => {
                console.log("Service Worker registered with scope:", registration.scope);
              },
              (error) => {
                console.error("Service Worker registration failed:", error);
              }
            );
          } else {
            console.error("Service Worker script is not accessible or has an unsupported MIME type.");
          }
        })
        .catch((error) => {
          console.error("Failed to fetch Service Worker script:", error);
        });
    });
  }
}

const bootstrapApp = async () => {
  // set icon path
  MdsConfig.iconsDynamicImportPath = "./";

  const config: MaerskVueAppConfig = {};
  config.selectedLang = "en";
  config.SKIP_GLOBAL_NAV = true;

  try {
    const response = await getMsalInstance().handleRedirectPromise();
    if (response) {
      console.debug("Redirect response received:", response);
      getMsalInstance().setActiveAccount(response.account);
    } else {
      const accounts = getMsalInstance().getAllAccounts();
      if (accounts.length > 0) {
        getMsalInstance().setActiveAccount(accounts[0]);
      }
    }

    setupAuthentication(getMsalInstance(), router);

    const app = createApp(App);
    const isLoaded = ref(false);
    app.provide("isLoaded", isLoaded);
    app.use(pinia);
    app.use(router);
    app.use(msalPlugin, getMsalInstance());
    app.use(VueDOMPurifyHTML);

    if (import.meta.env.DEV) {
      if (!import.meta.env.VITE_E2E) {
        // the icons path in development are different
        MdsConfig.iconsDynamicImportPath = "../../";
      }

      // This is a switch to the branding
      config.brandParam = "maeu"; // "maeu"; // values: 'seau', 'maeu', 'hsud' or 'maei';
    }

    const lang = "en-" + __BUILD_HASH__;

    app.use(MaerskVueApp, config);
    app.use(VueLanguageHandler, {
      langFilePathWeb: "/assets/languages",
      langFilePathLocal: "/assets/languages",
      currentLanguage: lang,
      doubleDelimiter: false
    });

    setupGlobals({
      ...app.config.globalProperties,
      version
    });

    app.mount("#legal-frontdoor");
    await router.isReady();
    await VueLanguageHandler.loadLanguageData();
    isLoaded.value = true;
    registerServiceWorker();
  } catch (error) {
    console.error("Error handling redirect:", error);
  }
};
bootstrapApp();
